/**
 * `logical_tables`, rendered as a real table.
 *
 * It reproduces the printed table's shape, which is what you compare against
 * an image: the printed column labels as headers, `is_total` rows in a
 * heavier weight, `level` as indentation, `note_ref` as a superscript.
 *
 * **Rendered right-to-left when the content is Arabic, regardless of the UI
 * language.** The table has to look like the paper (§10.4).
 */
import type { LogicalTable, NumberCell } from '@server/sql/page.ts'
import { Link } from 'wouter'
import { Raw, RawNumber, Untranslated } from '~/components/ui/raw.tsx'
import { Badge, Label } from '~/components/ui/primitives.tsx'
import { useT } from '~/i18n/index.tsx'
import { date } from '~/i18n/format.ts'
import { cn } from '~/lib/cn.ts'
import { pickDigits, useDigits } from './digits.tsx'

export function LogicalTableView({
  table, symbol, documentId,
}: { table: LogicalTable; symbol: string; documentId: number }) {
  const { t, locale, v } = useT()
  const rtl = table.lang === 'ar'

  /**
   * A printed note reference, resolved. Phase 9 works out that a superscript
   * `٩` means note 9 on page 12; without using that, the superscript is a
   * dead end and the reader has to go hunting.
   */
  const noteLink = (printed: string | null) => {
    if (!printed) return null
    const target = table.noteRefTargets[printed.trim()]
    if (!target || target.pageNos.length === 0) return null
    return {
      href: `/c/${symbol}/d/${documentId}?p=${target.pageNos[0]}`,
      title: t('viewer.goToNote', { n: target.noteNo ?? printed, p: target.pageNos[0]! }),
    }
  }

  return (
    <figure
      className="overflow-hidden rounded-[var(--radius-container)] border border-[var(--border)] bg-[var(--bg-raised)]"
      dir={rtl ? 'rtl' : 'ltr'}
      lang={table.lang ?? undefined}
    >
      <figcaption className="border-b border-[var(--border)] bg-[var(--bg-sunken)] px-2.5 py-1.5">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          {table.titleRaw ? (
            <Raw lang={table.lang} className="text-[length:var(--text-data)] font-medium text-[var(--text)]">
              {table.titleRaw}
            </Raw>
          ) : (
            <span className="text-[length:var(--text-data)] text-[var(--text-muted)]">
              {v('statementType', table.statement) ?? (
                <Untranslated>{table.statement ?? table.kind ?? '—'}</Untranslated>
              )}
            </span>
          )}
          {table.statement && table.titleRaw && (
            <span dir="ltr" className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
              {v('statementType', table.statement) ?? table.statement}
            </span>
          )}
          <div className="flex-1" />
          {/* Stitch's own accounting, surfaced rather than buried: a table
              rejoined across a page break is a place extractions go wrong. */}
          {table.merged.pages.length > 1 && (
            <Badge state="neutral" dir="ltr">
              {t('common.pages', { n: table.merged.pages.join(', ') })}
            </Badge>
          )}
          {table.merged.rejoinedWraps > 0 && (
            <span dir="ltr" className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
              {table.merged.rejoinedWraps} rejoined
            </span>
          )}
          {table.merged.droppedDuplicates > 0 && (
            <span dir="ltr" className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
              {table.merged.droppedDuplicates} dropped
            </span>
          )}
          {table.flags.map((f) => (
            <Badge key={f} state="neutral" dir="ltr">{f}</Badge>
          ))}
        </div>
        {table.unitsRaw && (
          <Raw lang={table.lang} className="mt-0.5 block text-[length:var(--text-micro)] text-[var(--text-faint)]">
            {table.unitsRaw}
          </Raw>
        )}
      </figcaption>

      {/*
        `w-max min-w-full`, not `w-full`.

        `w-full` inside `overflow-x-auto` is a contradiction: the table is
        pinned to the container width, so the scroller never has anything to
        scroll and the browser resolves the squeeze by shrinking columns
        instead. On a 16-column CMA remuneration table that is ~40px each,
        and long Arabic headings break to *one character per line* -- the
        header becomes a wall of vertical text.

        `min-w-full` keeps a narrow table filling the pane, `w-max` lets a
        wide one grow past it, and the scroller then does its job.
      */}
      <div className="scroll-x-hint overflow-x-auto overscroll-x-contain">
        <table className="w-max min-w-full border-collapse text-[length:var(--text-data)]">
          <thead>
            <tr>
              <th className="border-b border-[var(--border)] px-2 py-1 text-start" />
              {table.columns.map((c) => (
                <th
                  key={c.idx}
                  className="border-b border-s border-[var(--border)] px-2 py-1 text-end align-bottom font-medium"
                >
                  {/*
                    A floor so a heading is never squeezed below a word, and a
                    ceiling so one very long heading -- the CMA template has
                    several of 90+ characters -- does not push the rest off
                    the screen on its own. `whitespace-pre-line` keeps the
                    line breaks the page printed; `break-words` is what stops
                    an unbroken run from overflowing rather than wrapping.
                  */}
                  <Raw
                    lang={table.lang}
                    className="block min-w-[5.5rem] max-w-[14rem] whitespace-pre-line break-words text-[var(--text)]"
                  >
                    {c.labelRaw ?? `#${c.idx + 1}`}
                  </Raw>
                  {/* The period the pipeline resolved that label to. Shown
                      under it rather than instead of it: the printed label is
                      the thing you match against the image, and the resolved
                      date is the thing that could be wrong. */}
                  {c.periodEnd && (
                    <span
                      dir="ltr"
                      className="mt-0.5 block text-[length:var(--text-micro)] font-normal text-[var(--text-faint)]"
                    >
                      {date(c.periodEnd, locale)}
                      {c.assurance && ` · ${v('assurance', c.assurance) ?? c.assurance}`}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r) => (
              <tr
                key={r.order}
                className={cn(
                  'border-b border-[var(--border)] last:border-b-0',
                  r.isTotal && 'bg-[var(--bg-sunken)]/60',
                )}
              >
                <th
                  scope="row"
                  className={cn(
                    'px-2 py-[5px] text-start align-top font-normal',
                    r.isTotal && 'font-semibold',
                  )}
                  // `level` as indentation, in logical inset so it works in
                  // both directions.
                  style={{ paddingInlineStart: 8 + (r.level ?? 0) * 12 }}
                >
                  <Raw lang={table.lang} className="block min-w-[9rem] max-w-[22rem] break-words">
                    {r.labelRaw ?? ''}
                  </Raw>
                  {r.noteRef && <NoteRef printed={r.noteRef} lang={table.lang} link={noteLink(r.noteRef)} />}
                </th>
                {table.columns.map((c) => (
                  <td
                    key={c.idx}
                    className={cn(
                      'border-s border-[var(--border)] px-2 py-[5px] text-end align-top',
                      'whitespace-nowrap',
                      r.isTotal && 'font-semibold',
                    )}
                  >
                    <Cell cell={r.values[c.idx] ?? null} lang={table.lang} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {table.footerRaw && (
        <Raw
          lang={table.lang}
          className="block border-t border-[var(--border)] px-2.5 py-1.5 text-[length:var(--text-micro)] text-[var(--text-muted)]"
        >
          {table.footerRaw}
        </Raw>
      )}

      {/* Who signed the statement, laid out the way the page lays it out:
          a rule per signatory with the role beneath it. The rule stands for
          the signature, which is handwritten and lives in the image next to
          this pane -- so the block is a map to it, not a claim to have read
          it. Printed at the foot of every such page, and until now read by
          the pipeline and then thrown away by this screen. */}
      {table.signatories.length > 0 && (
        <div className="border-t border-[var(--border)] px-2.5 pb-2 pt-2.5">
          <Label>{t('viewer.signatories')}</Label>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-3">
            {table.signatories.map((role, i) => (
              <div key={i} className="min-w-[8rem] flex-1 basis-32">
                <div
                  aria-hidden="true"
                  className="h-5 border-b border-dashed border-[var(--border-strong)]"
                />
                <Raw
                  lang={table.lang}
                  className="mt-1 block text-center text-[length:var(--text-tiny)] leading-[var(--text-micro--line-height)] text-[var(--text)]"
                >
                  {role}
                </Raw>
              </div>
            ))}
          </div>
          <p className="mt-1.5 text-[length:var(--text-micro)] leading-[var(--text-micro--line-height)] text-[var(--text-faint)]">
            {t('viewer.signatoriesHint')}
          </p>
        </div>
      )}
    </figure>
  )
}

function Cell({ cell, lang }: { cell: NumberCell | null; lang: string | null }) {
  const { t } = useT()
  const mode = useDigits()

  if (!cell) return <span className="text-[var(--text-faint)]">·</span>
  // A printed dash is a dash. It is not a zero, and it is not missing.
  if (cell.isDash) return <span className="text-[var(--text-muted)]">—</span>

  const { text, isFallback } = pickDigits(mode, cell.raw, cell.western)
  if (text === null) return <span className="text-[var(--text-faint)]">·</span>

  // The stored string already carries its brackets -- `(٤٣٬٢١٨٬٦١٨)`.
  // `in_parens` is a flag *about* that string, recording that the pipeline
  // read it as a negative; it is not an instruction to draw brackets. Adding
  // them here produced `((٤٣٬٢١٨٬٦١٨))`, which is precisely the reformatting
  // this pane exists not to do. The flag is surfaced where it belongs, in the
  // fact audit view, next to the signed value it produced.
  return (
    <RawNumber
      value={text}
      lang={isFallback ? null : lang}
      className={cn(
        // A transliteration the pipeline filled in deterministically, because
        // the model stopped transliterating, is worth seeing.
        cell.derivedWestern && 'underline decoration-dotted decoration-[var(--warn)] underline-offset-2',
      )}
      title={cell.derivedWestern ? t('fact.derivedWestern') : undefined}
    />
  )
}

/**
 * A superscript note reference. A link where phase 9 resolved it to a page,
 * plain text where it did not -- an unresolved reference is a real state and
 * should not be dressed as a working link.
 */
function NoteRef({
  printed, lang, link,
}: {
  printed: string
  lang: string | null
  link: { href: string; title: string } | null
}) {
  const inner = <Raw lang={lang}>{printed}</Raw>
  return (
    <sup className="ms-1 text-[length:var(--text-micro)]">
      {link ? (
        <Link
          href={link.href}
          title={link.title}
          className="link-inline"
        >
          {inner}
        </Link>
      ) : (
        <span className="text-[var(--text-faint)]">{inner}</span>
      )}
    </sup>
  )
}
